<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { KitUpdateDocument, KitType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';
import { formatDate, formatDateStringToIso } from '@/utils/date-utils';

// --- Component properties ---
const props = defineProps<{
  kit: KitType;
  show: boolean;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});
const newKitName = ref<string | null>(props.kit.name || null);
const newSampleCount = ref<number | null>(props.kit.sampleCount || null);
const newManufacturedAt = ref<Date | null>(props.kit.manufacturedAt);
const clearManufacturingDate = ref<boolean>(props.kit.manufacturedAt !== null);
const newExpirationMonths = ref<number | null>(
  props.kit.expirationMonths || null
);
const newDescription = ref<string | null>(props.kit.description || null);
const newVersion = ref<string | null>(props.kit.version || null);
const newBarcode = ref<string | null>(props.kit.barcode || null);
const newUmiLength = ref<number>(props.kit.umiLength || 8);

// --- Mutation ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(KitUpdateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.kitUpdate) {
    console.debug('Kit updated:', res.data.kitUpdate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Kit update failed: ' + res.data;
    console.error('Kit update failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error updating kit:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function close() {
  emits('close');
}

function updateRequested() {
  console.debug('Update requested');

  if (!props.kit.id) {
    mutationError.value = 'Kit ID is missing';
    console.error('Kit ID is missing');
    return;
  }

  let manufacturingDate: string | null = null;

  if (!clearManufacturingDate.value) {
    manufacturingDate = formatDateStringToIso(newManufacturedAt.value);
  }

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({
    id: props.kit.id,
    sampleCount: newSampleCount.value,
    name: newKitName.value,
    description: newDescription.value,
    version: newVersion.value,
    barcode: newBarcode.value,
    umiLength: newUmiLength.value,
    manufacturedAt: manufacturingDate,
    clearManufacturingDate: clearManufacturingDate.value,
    expirationMonths: newExpirationMonths.value,
  });
}
</script>
<template>
  <div>
    <BModal v-model="show" @hide="close">
      <!-- Modal Header -->
      <template #title>
        <font-awesome-icon :icon="['fas', 'cube']" class="me-2" />
        {{ kit.name }} kit
      </template>

      <!-- Modal Body-->
      <div>
        <h5>Current state</h5>
        <p>
          <strong>Samples: </strong> {{ kit.sampleCount }} samples<br />
          <strong>Barcode: </strong> {{ kit.barcode }}<br />
          <strong>Manufactured: </strong>
          {{ formatDate(kit.manufacturedAt, 'not specified') }}<br />
          <strong>Expiration: </strong> {{ kit.expirationMonths }} months<br />
          <strong>Description: </strong>{{ kit.description }}<br />
          <strong>Version: </strong>
          {{ kit.version ? kit.version : 'not specified' }}
        </p>

        <h5>Update kit</h5>
        <!-- Kit name -->
        <BFormGroup label="Kit name" label-for="kit-name" class="mt-3">
          <BFormInput
            v-model="newKitName"
            id="kit-name"
            name="kit-name"
            placeholder="Enter the kit name"
          ></BFormInput>
        </BFormGroup>

        <!-- Kit description -->
        <BFormGroup
          label="Kit description"
          label-for="kit-description"
          class="mt-3"
        >
          <BFormTextarea
            v-model="newDescription"
            id="kit-description"
            name="kit-description"
            placeholder="Enter a new kit description"
          ></BFormTextarea>
        </BFormGroup>

        <!-- Kit version -->
        <BFormGroup label="Kit version" label-for="kit-version" class="mt-3">
          <BFormInput
            v-model="newVersion"
            id="kit-version"
            name="kit-version"
            placeholder="Enter the kit version"
          ></BFormInput>
        </BFormGroup>

        <!-- Sample Count -->
        <BFormGroup
          label="Number of samples"
          label-for="sample-count"
          class="mt-3"
        >
          <BFormInput
            v-model.number="newSampleCount"
            id="sample-count"
            name="sample-count"
            type="number"
            placeholder="Enter the number of samples"
            min="0"
          ></BFormInput>
        </BFormGroup>

        <!-- Kit barcode -->
        <BFormGroup label="Kit barcode" label-for="kit-barcode" class="mt-3">
          <BFormInput
            v-model="newBarcode"
            id="kit-barcode"
            name="kit-barcode"
            placeholder="Enter the barcode"
          ></BFormInput>
        </BFormGroup>

        <!-- UMI length -->
        <BFormGroup label="UMI length" label-for="umi-length" class="mt-3">
          <BFormInput
            v-model.number="newUmiLength"
            id="umi-length"
            name="umi-length"
            placeholder="Enter UMI length"
            type="number"
          ></BFormInput>
        </BFormGroup>

        <!-- Manufacturing Date -->
        <BFormGroup
          label="Manufacturing date"
          label-for="manufacturing-date"
          class="mt-3"
        >
          <BFormInput
            v-model="newManufacturedAt"
            id="manufacturing-date"
            name="manufacturing-date"
            placeholder="Enter a new expiration date"
            type="date"
            :disabled="clearManufacturingDate"
          ></BFormInput>
        </BFormGroup>

        <!-- Clear Manufacturing Date Checkbox -->
        <BFormGroup class="mt-3">
          <BFormCheckbox
            v-model="clearManufacturingDate"
            id="clear-manufacturing-date"
            name="clear-manufacturing-date"
          >
            Clear manufacturing date
          </BFormCheckbox>
        </BFormGroup>

        <!-- Expiration Months -->
        <BFormGroup
          label="Expiration months"
          label-for="expiration-months"
          class="mt-3"
        >
          <BFormInput
            v-model.number="newExpirationMonths"
            id="expiration-months"
            name="expiration-months"
            type="number"
            placeholder="Enter the number of expiration months"
            min="0"
          ></BFormInput>
        </BFormGroup>

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>

      <!-- Modal Footer -->
      <template #footer>
        <BButton @click="close" variant="secondary"> Close </BButton>
        <BButton
          @click="updateRequested"
          :disabled="mutationInProgress || mutationShowSuccess"
          variant="primary"
        >
          <span v-if="mutationInProgress">
            Updating Kit
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Kit Updated
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Update Kit </span>
        </BButton>
      </template>
    </BModal>
  </div>
</template>
